// GeneradorGastoModal.js — formulario de revisión tras pulsar "Generar
// gasto". Muestra el PDF junto a los datos propuestos por la IA; nada se
// guarda hasta pulsar "Guardar gasto" (ver guardarGastoProveedor.js).
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import { CATEGORIAS_GASTO } from './ReglasGastosPanel';

function fmtEur(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function GeneradorGastoModal({ item, onClose, onSaved }) {
  const [propuesta, setPropuesta] = useState(item.propuesta || null);
  const [generando, setGenerando] = useState(!item.propuesta);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [movimientoElegido, setMovimientoElegido] = useState(null);
  const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

  const generar = useCallback(async () => {
    setGenerando(true); setError('');
    try {
      const res = await fetch('/.netlify/functions/generarGastoDesdeBandeja', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: item.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setPropuesta(data.propuesta);
      setMovimientoElegido(null);
      setConfirmarDuplicado(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerando(false);
    }
  }, [item.id]);

  useEffect(() => { if (!propuesta) generar(); }, [propuesta, generar]);

  const campo = (k, v) => setPropuesta(p => ({ ...p, [k]: v }));
  const campoIva = (i, k, v) => setPropuesta(p => {
    const iva = [...(p.iva || [])];
    iva[i] = { ...iva[i], [k]: Number(v) || 0 };
    return { ...p, iva };
  });

  const handleGuardar = async () => {
    if (!propuesta) return;
    setGuardando(true); setError('');
    try {
      const res = await fetch('/.netlify/functions/guardarGastoProveedor', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          bandejaId: item.id,
          datos: {
            proveedor: propuesta.proveedor, nif: propuesta.nif, numeroFactura: propuesta.numeroFactura,
            fecha: propuesta.fecha, baseImponible: Number(propuesta.baseImponible) || 0,
            iva: propuesta.iva || [], retencion: Number(propuesta.retencion) || 0, total: Number(propuesta.total) || 0,
            lineas: propuesta.lineas || [], categoria: propuesta.categoria,
          },
          movimientoBancoId: movimientoElegido,
          confirmarDuplicado,
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.duplicado) {
        setError('Ya existe un gasto con este NIF y número de factura. Marca la casilla de abajo si quieres guardarlo igual.');
        return;
      }
      if (!data.success) throw new Error(data.error);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const v = propuesta?.validaciones;
  const avisos = [];
  if (v && !v.nifOk)    avisos.push('El NIF no tiene un formato válido.');
  if (v && !v.fechaOk)  avisos.push('La fecha de factura no parece razonable — revísala.');
  if (v && !v.sumaOk)   avisos.push('Base + IVA − retención no coincide con el total.');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900">{item.nombreArchivo}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          <div className="w-1/2 border-r bg-gray-50">
            {item.pdfUrl
              ? <iframe title="PDF" src={item.pdfUrl} className="w-full h-full" />
              : <div className="flex items-center justify-center h-full text-gray-400 text-sm">Sin PDF</div>}
          </div>

          <div className="w-1/2 overflow-y-auto p-5 space-y-4">
            {generando && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
                <p className="text-sm text-gray-400">Extrayendo datos con IA…</p>
              </div>
            )}

            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

            {!generando && propuesta && (
              <>
                {avisos.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-xs text-amber-700 space-y-0.5">
                    {avisos.map((a, i) => <p key={i}>⚠️ {a}</p>)}
                  </div>
                )}

                {propuesta.duplicado && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-xs text-red-700 space-y-1.5">
                    <p>⚠️ Ya existe un gasto registrado con este NIF y nº de factura ({fmtEur(propuesta.duplicado.total)}, {propuesta.duplicado.fecha}).</p>
                    <label className="flex items-center gap-1.5 font-semibold">
                      <input type="checkbox" checked={confirmarDuplicado} onChange={e => setConfirmarDuplicado(e.target.checked)} />
                      Guardar igual (sé que es un duplicado)
                    </label>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <label className="col-span-2 text-xs font-semibold text-gray-500">
                    Proveedor
                    <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.proveedor || ''} onChange={e => campo('proveedor', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    NIF
                    <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1 font-mono" value={propuesta.nif || ''} onChange={e => campo('nif', e.target.value.toUpperCase())} />
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    Nº factura
                    <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.numeroFactura || ''} onChange={e => campo('numeroFactura', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    Fecha
                    <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.fecha || ''} onChange={e => campo('fecha', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    Categoría
                    <select className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.categoria || ''} onChange={e => campo('categoria', e.target.value)}>
                      <option value="">— sin proponer —</option>
                      {CATEGORIAS_GASTO.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    Base imponible
                    <input type="number" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.baseImponible ?? 0} onChange={e => campo('baseImponible', Number(e.target.value))} />
                  </label>
                  <label className="text-xs font-semibold text-gray-500">
                    Retención
                    <input type="number" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={propuesta.retencion ?? 0} onChange={e => campo('retencion', Number(e.target.value))} />
                  </label>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">IVA</p>
                  <div className="space-y-1.5">
                    {(propuesta.iva || []).map((t, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input type="number" className="w-20 border rounded-lg px-2 py-1.5 text-xs" value={t.tipo} onChange={e => campoIva(i, 'tipo', e.target.value)} />
                        <span className="text-xs text-gray-400">% →</span>
                        <input type="number" step="0.01" className="w-28 border rounded-lg px-2 py-1.5 text-xs" value={t.cuota} onChange={e => campoIva(i, 'cuota', e.target.value)} />
                        <span className="text-xs text-gray-400">€</span>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="block text-xs font-semibold text-gray-500">
                  Total
                  <input type="number" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm mt-1 font-bold" value={propuesta.total ?? 0} onChange={e => campo('total', Number(e.target.value))} />
                </label>

                {propuesta.movimientosSugeridos?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">¿Enlazar con un movimiento del banco?</p>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-xs">
                        <input type="radio" name="mov" checked={movimientoElegido === null} onChange={() => setMovimientoElegido(null)} /> No enlazar
                      </label>
                      {propuesta.movimientosSugeridos.map(m => (
                        <label key={m.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1.5">
                          <input type="radio" name="mov" checked={movimientoElegido === m.id} onChange={() => setMovimientoElegido(m.id)} />
                          {m.fecha} — {m.concepto} — {fmtEur(Math.abs(m.importe))}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {propuesta.lineas?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">Líneas</p>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      {propuesta.lineas.map((l, i) => <p key={i} className="flex justify-between"><span>{l.descripcion}</span><span>{fmtEur(l.importe)}</span></p>)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <button onClick={generar} disabled={generando} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 disabled:opacity-60">
            {generando ? 'Generando…' : 'Regenerar con IA'}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-500 text-xs font-bold rounded-xl hover:bg-gray-100">Cancelar</button>
            <button onClick={handleGuardar} disabled={guardando || generando || !propuesta} className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-60">
              {guardando ? 'Guardando…' : 'Guardar gasto'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
